import React from 'react'
import { useField } from 'formik'
import Radio from '../../Radio/Radio'
import { modeTypes } from '../constants'
import SelectInlineFooter from './SelectInlineFooter'
import {
  Choice,
  getChoiceLabel,
  withDefaultsFirst,
  SELECT_FIELD_INLINE_CHOICE_LIMIT
} from './selectFieldHelpers'

interface RadioFieldProps {
  field: any
  mode?: string
}

/**
 * Single-select rendered as inline radio buttons (asset-form `display_type = RADIO`).
 * Reads/writes the selected choice `value` through Formik; a `None` radio unsets it.
 */
const RadioField: React.FC<RadioFieldProps> = ({ field, mode }) => {
  const { formFieldUid } = field
  const [inputField, , helpers] = useField(formFieldUid)
  const value: string | null = inputField.value ?? null
  const isReadOnly = mode === modeTypes.readOnly || field.readOnly

  const isKeyValue = !!field?.field_metadata?.is_key_value
  const choices: Choice[] = withDefaultsFirst(field?.field_metadata?.choices || [])
  const overLimit = choices.length > SELECT_FIELD_INLINE_CHOICE_LIMIT
  const inlineChoices = overLimit ? choices.slice(0, SELECT_FIELD_INLINE_CHOICE_LIMIT) : choices

  return (
    <div
      className="asset-details-v2-fields-container__select-field"
      data-test-id={`cs-asset-details-field-${formFieldUid}-wrapper`}
    >
      {inlineChoices.map((choice) => (
        <Radio
          key={choice.value}
          name={formFieldUid}
          radioDisplayType="block"
          value={choice.value}
          checked={value === choice.value}
          disabled={isReadOnly}
          onChange={() => helpers.setValue(choice.value)}
          label={getChoiceLabel(choice, isKeyValue)}
          testId={`cs-asset-details-field-${formFieldUid}-radio-${choice.value}`}
        />
      ))}
      <SelectInlineFooter
        fieldUid={formFieldUid}
        choices={choices}
        isKeyValue={isKeyValue}
        isMultiple={false}
        currentValue={value}
        isReadOnly={isReadOnly}
        hasValue={value != null && value !== ''}
        title={field?.title}
        onApply={(v) => helpers.setValue(v)}
        onClear={() => helpers.setValue(null)}
      />
    </div>
  )
}

export default RadioField
